import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useGetPatientActivityQuery } from '@/store/slices/api/activityFeedApiSlice';
import {
  resetActivityFeedFilters,
  setActivityFeedFilters as setFilterOptions
} from '@/store/slices/preferencesSlice';

import { ActivityFeedItem } from '@/types/activityFeed';

const PAGE_SIZE = 10;
const INITIAL_PAGE = 0;

export const useActivityFeed = (patientId: string) => {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [activityItems, setActivityItems] = useState<ActivityFeedItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const isInitialLoad = useRef(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const processedPages = useRef(new Set<number>());

  const { activityFeedFilters: filterOptions } = useAppSelector((state) => state.preferences);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!filterOptions || filterOptions.length === 0) {
      dispatch(resetActivityFeedFilters());
    }
  }, [filterOptions, dispatch]);

  const {
    data: activityData,
    isLoading,
    isError,
    isFetching
  } = useGetPatientActivityQuery(
    {
      patientId,
      filters: filterOptions,
      page,
      pageSize: PAGE_SIZE
    },
    {
      skip: !patientId
    }
  );

  // Handle fetching states
  useEffect(() => {
    if (isFetching) {
      setIsFetchingMore(page > INITIAL_PAGE);
    } else {
      setIsFetchingMore(false);
      if (page === INITIAL_PAGE) {
        isInitialLoad.current = false;
        // Clear filter loading state when initial data load completes
        setIsFilterLoading(false);
      }
    }
  }, [isFetching, page]);

  // Update activity items when data arrives
  useEffect(() => {
    if (!activityData || isFetching) return;

    const newData = activityData.data || [];

    if (page === INITIAL_PAGE) {
      // Always update the first page, even if it was processed before.
      setActivityItems(newData);
      processedPages.current = new Set([INITIAL_PAGE]);
    } else {
      // For subsequent pages, only add if not processed before.
      if (processedPages.current.has(page)) return;
      setActivityItems((prev) => [...prev, ...newData]);
      processedPages.current.add(page);
    }

    setHasMore(newData.length === PAGE_SIZE);
  }, [activityData, page, isFetching]);

  // Reset processed pages when filters change
  useEffect(() => {
    processedPages.current = new Set();
    setPage(INITIAL_PAGE);
  }, [filterOptions]);

  // Setup intersection observer for infinite scroll
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading || isFetchingMore || !hasMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !isFetching) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, isFetchingMore, hasMore, isFetching]
  );

  // Toggle drawer state – simply toggles without resetting data
  const toggleDrawer = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Update filter options using current state from Redux
  const updateFilterOptions = useCallback(
    (ids: string[]) => {
      // Get the most current filterOptions directly from the store
      const currentFilterOptions = filterOptions;

      // Don't set loading state if options are the same
      const areOptionsSame =
        ids.length === currentFilterOptions.length &&
        ids.every((id) => currentFilterOptions.includes(id));

      if (areOptionsSame) return;

      setIsFilterLoading(true);
      dispatch(setFilterOptions(ids));
      setPage(INITIAL_PAGE);
      setActivityItems([]);
      processedPages.current = new Set();

      // Keep loading state active until we get new data or time out
      const loadingTimeout = setTimeout(() => {
        setIsFilterLoading(false);
      }, 2000); // Ensure loading state clears even if query fails

      return () => clearTimeout(loadingTimeout);
    },
    [dispatch, filterOptions]
  );

  return {
    activityItems,
    isLoading,
    isFetchingMore,
    isError,
    isOpen,
    toggleDrawer,
    lastElementRef,
    updateFilterOptions,
    filterOptions,
    hasMore,
    isFilterLoading,
    isInitialLoad: isInitialLoad.current
  };
};
